/* eslint-disable no-console */
import * as Sentry from '@sentry/react'

const isDev = process.env.NODE_ENV !== 'production'

const formatSentryMessage = (value: unknown): string => {
  if (typeof value === 'string') return value
  if (value instanceof Error) return value.message
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

const sentryCapture = (value: unknown, level: Sentry.SeverityLevel, context?: string): void => {
  Sentry.withScope((scope) => {
    scope.setLevel(level)
    if (context) scope.setTag('context', context)

    if (value instanceof Error) {
      Sentry.captureException(value)
    } else {
      const message = context ? `[${context}] ${formatSentryMessage(value)}` : formatSentryMessage(value)
      Sentry.captureMessage(message)
    }
  })
}

export const logger = {
  /** Operational events — dev only, silent in production. */
  log: (message: unknown, context?: string): void => {
    if (!isDev) return
    if (context) {
      console.log(`[${context}]`, message)
    } else {
      console.log(message)
    }
  },

  /** Verbose debug info — dev only, silent in production. */
  debug: (message: unknown, context?: string): void => {
    if (!isDev) return
    if (context) {
      console.debug(`[${context}]`, message)
    } else {
      console.debug(message)
    }
  },

  /** Informational events — console in dev, Sentry 'info' in production. */
  info: (message: unknown, context?: string): void => {
    if (isDev) {
      if (context) {
        console.info(`[${context}]`, message)
      } else {
        console.info(message)
      }
    } else {
      sentryCapture(message, 'info', context)
    }
  },

  /** Non-critical warnings — console in dev, Sentry 'warning' in production. */
  warn: (message: unknown, context?: string): void => {
    if (isDev) {
      if (context) {
        console.warn(`[${context}]`, message)
      } else {
        console.warn(message)
      }
    } else {
      sentryCapture(message, 'warning', context)
    }
  },

  /** Errors — console in dev, Sentry 'error' in production. */
  error: (message: unknown, context?: string): void => {
    if (isDev) {
      if (context) {
        console.error(`[${context}]`, message)
      } else {
        console.error(message)
      }
    } else {
      sentryCapture(message, 'error', context)
    }
  },
}
